import React, { useMemo } from 'react';
import { Table } from 'equal-ds-ui';
import type { TableColumn } from 'equal-ds-ui';
import type { AAConversionByFIPResponse, FIPBreakdown } from '../../../types/smart-router';

interface Props { data: AAConversionByFIPResponse; level: 'consent' | 'user'; }

const convBadge = (value: number) => {
  const cls = value >= 75 ? 'bg-green-50 text-green-700' : value >= 65 ? 'bg-yellow-50 text-yellow-700' : 'bg-red-50 text-red-700';
  return <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${cls}`}>{value}%</span>;
};

export const FipPerformanceTable: React.FC<Props> = ({ data, level }) => {
  const fipBreakdowns = data?.fip_breakdowns ?? [];
  const aaIds = Array.from(new Set(fipBreakdowns.flatMap(f => (f.conversions ?? []).map(c => c.aa_id))));

  const columns = useMemo<TableColumn<FIPBreakdown>[]>(() => [
    { key: 'fip_id', header: 'FIP', width: 120, accessor: (row) => row.fip_id },
    ...aaIds.map(aaId => ({
      key: aaId,
      header: aaId,
      width: 120,
      render: (_val: any, row: FIPBreakdown) => {
        const conv = row.conversions?.find(c => c.aa_id === aaId);
        return conv ? convBadge(conv.success_rate) : <span className="text-text-tertiary">-</span>;
      },
    })),
  ], [aaIds]);

  return (
    <div className="border border-border-default rounded-xl shadow-md overflow-hidden">
      <div className="bg-gradient-to-b from-primary-50 to-primary-100 px-4 py-3">
        <h2 className="text-sm font-semibold text-text-primary tracking-wider">FIP Performance Across AAs</h2>
        <p className="text-xs text-text-tertiary mt-0.5">{level === 'consent' ? 'Consent' : 'User'} level conversion rates</p>
      </div>
      <div className="bg-white p-5">
        <Table<FIPBreakdown> columns={columns} data={fipBreakdowns} />
      </div>
    </div>
  );
};
